import { useMemo } from "react";
import { ChartFrame, type TableColumn } from "./chart-frame";
import { STATUS } from "./tokens";
import type { AttendanceRecord, LeaveDay, WfhDay } from "@/api/types";
import { CELL_LABEL, classifyDay, type CellKind } from "@/lib/calendar";
import { LEAVE_TYPE_SHORT } from "@/lib/labels";
import { formatDate } from "@/lib/format";

/**
 * A person × day grid. This is a *categorical state* grid, not a magnitude
 * heatmap, so cells wear the reserved status tokens (each paired with a label in
 * the legend and the tooltip) rather than a sequential ramp — the underlying
 * value is a state, not a quantity.
 */
const CELL_COLOR: Record<CellKind, string> = {
  complete: STATUS.good,
  half: STATUS.warn,
  inactive: "rgb(var(--line-strong))",
  absent: STATUS.bad,
  rest: "rgb(var(--line-strong))",
  holiday: "rgb(var(--line-strong))",
  future: "rgb(var(--line))",
  leave: STATUS.leave,
  "leave-pending": STATUS.leavePending,
  wfh: STATUS.wfh,
};

interface AttendanceHeatmapProps {
  title: string;
  note?: string;
  people: { id: number; name: string | null }[];
  records: AttendanceRecord[];
  dates: string[];
  holidays: Map<string, string>;
  /** Live leave, keyed `${user_id}|${date}` — see lib/calendar.ts's leaveMap. */
  leave?: Map<string, LeaveDay>;
  /** Live work-from-home, keyed the same way — see lib/calendar.ts's wfhMap. */
  wfh?: Map<string, WfhDay>;
  loading?: boolean;
  error?: string | null;
  emptyBody?: string;
  className?: string;
}

export function AttendanceHeatmap({
  title,
  note,
  people,
  records,
  dates,
  holidays,
  leave,
  wfh,
  loading,
  error,
  emptyBody,
  className,
}: AttendanceHeatmapProps) {
  const byPersonDate = useMemo(() => {
    const map = new Map<string, AttendanceRecord>();
    for (const record of records) map.set(`${record.user_id}|${record.date}`, record);
    return map;
  }, [records]);

  const grid = useMemo(
    () =>
      people.map((person) => ({
        person,
        cells: dates.map((date) => {
          const key = `${person.id}|${date}`;
          const onLeave = leave?.get(key);
          const fromHome = wfh?.get(key);
          return {
            date,
            kind: classifyDay(date, byPersonDate.get(key), holidays, onLeave, fromHome),
            leave: onLeave,
            wfh: fromHome,
          };
        }),
      })),
    [people, dates, byPersonDate, holidays, leave, wfh],
  );

  // The table twin counts each state per person, which is the readable form of
  // a grid this dense.
  const columns: TableColumn[] = [
    { key: "name", header: "Person" },
    { key: "complete", header: "Full days", numeric: true },
    { key: "half", header: "Half days", numeric: true },
    { key: "leave", header: "On leave", numeric: true },
    { key: "absent", header: "No record", numeric: true },
    { key: "rest", header: "Off", numeric: true },
  ];

  const rows = grid.map(({ person, cells }) => {
    const tally = { complete: 0, half: 0, leave: 0, absent: 0, rest: 0 };
    for (const cell of cells) {
      if (cell.kind === "complete") tally.complete += 1;
      else if (cell.kind === "half") tally.half += 1;
      else if (cell.kind === "leave") tally.leave += 1;
      // A declared day off, and a request nobody has decided yet, count with
      // "no record" here: none is worked time, and none is authorised absence.
      else if (cell.kind === "absent" || cell.kind === "inactive" || cell.kind === "leave-pending")
        tally.absent += 1;
      else if (cell.kind === "rest" || cell.kind === "holiday") tally.rest += 1;
      // 'wfh' is a day still to come, so it belongs in none of these columns:
      // counting a plan as worked, absent or off would each be a different lie.
      // A WFH day that has been worked is already in `complete` or `half`.
    }
    return {
      name: person.name ?? "Unnamed",
      complete: String(tally.complete),
      half: String(tally.half),
      leave: String(tally.leave),
      absent: String(tally.absent),
      rest: String(tally.rest),
    };
  });

  // Pending only earns a legend swatch when the grid actually contains one —
  // a key to a state nothing on screen is in is just one more thing to read.
  const hasPending = grid.some(({ cells }) => cells.some((cell) => cell.kind === "leave-pending"));
  const hasWfh = grid.some(({ cells }) => cells.some((cell) => cell.kind === "wfh"));
  const legendKinds: CellKind[] = [
    "complete",
    "half",
    "leave",
    ...(hasPending ? (["leave-pending"] as CellKind[]) : []),
    ...(hasWfh ? (["wfh"] as CellKind[]) : []),
    "absent",
    "rest",
  ];
  const legend = legendKinds.map((kind) => ({
    label: CELL_LABEL[kind],
    color: CELL_COLOR[kind],
  }));

  return (
    <ChartFrame
      title={title}
      {...(note ? { note } : {})}
      legend={legend}
      table={{ columns, rows }}
      {...(loading ? { loading } : {})}
      {...(error ? { error } : {})}
      empty={!loading && !error && people.length === 0}
      {...(emptyBody ? { emptyBody } : {})}
      {...(className ? { className } : {})}
    >
      <div className="overflow-x-auto">
        <div className="min-w-max">
          <div className="mb-1.5 flex items-end gap-px pl-[140px]">
            {dates.map((date) => (
              <span key={date} className="w-5 text-center font-mono text-[10px] leading-none text-faint">
                {date.slice(-2)}
              </span>
            ))}
          </div>
          <div className="flex flex-col gap-px">
            {grid.map(({ person, cells }) => (
              <div key={person.id} className="flex items-center gap-px">
                <span className="w-[140px] shrink-0 truncate pr-3 text-xs text-ink-soft">
                  {person.name ?? "Unnamed"}
                </span>
                {cells.map((cell) => (
                  <span
                    key={cell.date}
                    /* The 1px flex gap plus the rounded corner is the surface
                       separation; no stroke is drawn around cells. */
                    className="h-5 w-5 rounded-[3px]"
                    style={{ backgroundColor: CELL_COLOR[cell.kind] }}
                    /* A day WORKED from home keeps its own colour (it was a full
                       or half day like any other) and says so here instead —
                       which is the only place the fact would otherwise be lost. */
                    title={`${person.name ?? "Unnamed"} · ${formatDate(cell.date)} · ${
                      holidays.get(cell.date) ?? CELL_LABEL[cell.kind]
                    }${cell.leave ? ` (${LEAVE_TYPE_SHORT[cell.leave.leave_type] ?? cell.leave.leave_type})` : ""}${
                      cell.wfh?.status === "approved" && cell.kind !== "wfh" ? " · from home" : ""
                    }`}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </ChartFrame>
  );
}
